import { ReadonlyMat3 } from 'gl-matrix';
import {
    PatientPosition,
    patientPositionFromOrientation,
} from '~/utils/orientation';

/** The four standard axial setups the couch premise is stated over. */
const STANDARD_POSITIONS: readonly string[] = [
    'HFS',
    'HFP',
    'FFS',
    'FFP',
] satisfies readonly PatientPosition[];

/**
 * Decubitus setups. `patientPositionFromOrientation` never emits these —
 * it models the four axial setups only — so a decubitus tag can never
 * AGREE with an implied position. That asymmetry is evidence rather than
 * noise: an acquired decubitus series has a permutation grid, for which
 * the lookup returns null and nothing here fires, so reaching a non-null
 * implied position at all means the grid no longer carries the recorded
 * pose.
 */
const DECUBITUS_POSITIONS: readonly string[] = ['HFDR', 'HFDL', 'FFDR', 'FFDL'];

/**
 * Whether the series' RECORDED patient position contradicts the position
 * its grid orientation implies.
 *
 * Couch placement works in voxel space and needs no per-pose special
 * casing because of one geometric premise: for ACQUIRED axial data the
 * image column direction points toward the treatment table. A prone
 * acquisition carries ImageOrientationPatient (-1,0,0, 0,-1,0), so image
 * +Y is patient anterior, which is exactly where the couch physically
 * is; extruding the couch down the image therefore moves it away from
 * the patient in every pose.
 *
 * A reformat voids that premise. MPR axial reslices into a neutral HFS
 * grid whatever the patient's actual pose, while the rewrite
 * deliberately PRESERVES the source PatientPosition tag. For a
 * reformatted prone series image +Y is then patient POSTERIOR with the
 * couch still anterior, so extrusion drives the couch INTO the patient
 * and writes density overrides onto tissue. The grid is axis-aligned,
 * untilted and CT throughout, so no other gate sees anything wrong: this
 * disagreement between two independently recorded facts is the only
 * signal that the geometry has been reslid out from under the premise.
 *
 * Assumption when a side is unavailable: we do NOT report a mismatch.
 * PatientPosition (0018,5100) is optional and frequently absent, and
 * `patientPositionFromOrientation` returns null for oblique and
 * otherwise non-standard matrices. Neither case establishes
 * disagreement, and treating them as one would disable the couch tool on
 * ordinary series for want of an optional tag; obliquity is gated on its
 * own elsewhere. Only two KNOWN values that differ count. A recorded
 * position we do not model at all (vendor spellings, seated setups) is
 * unavailable in the same sense. Decubitus is NOT: see
 * {@link DECUBITUS_POSITIONS}.
 *
 * @param recordedPatientPosition DICOM PatientPosition (0018,5100) as
 *   stored on the series, or undefined when the tag is absent.
 * @param orientation The grid's 3x3 orientation matrix.
 */
export function isPatientPositionInconsistentWithGrid(
    recordedPatientPosition: string | undefined,
    orientation: ReadonlyMat3
): boolean {
    const recorded = recordedPatientPosition?.trim().toUpperCase();
    if (
        !recorded ||
        !(
            STANDARD_POSITIONS.includes(recorded) ||
            DECUBITUS_POSITIONS.includes(recorded)
        )
    ) {
        return false;
    }
    const implied = patientPositionFromOrientation(orientation);
    if (implied === null) {
        return false;
    }
    return implied !== recorded;
}
